import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import InteractiveDashboardCard from '../components/InteractiveDashboardCard';
import { RecruitmentMetrics, Requisition, RecruiterAlert } from '../types';
import { mockRecruitmentData } from '../utils/mockRecruitmentData';
import { mockMarketIntelligence, getSentimentColor, getDifficultyColor } from '../utils/mockMarketIntelligence';

const DashboardContainer = styled.div`
  padding: ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  background-color: ${Colors.background};
`;

const KPIRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: ${Spacing.xl}px;
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px;
`;

const SectionCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const ViewAllButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.primary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
  }
`;

const RequisitionsList = styled.div`
  padding: ${Spacing.md}px;
`;

const RequisitionItem = styled.div`
  padding: ${Spacing.md}px;
  border-radius: ${BorderRadius.md}px;
  background-color: ${Colors.background};
  margin-bottom: ${Spacing.md}px;
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
    transform: translateX(4px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RequisitionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${Spacing.sm}px;
`;

const RequisitionTitle = styled.h3`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin: 0;
`;

const PriorityBadge = styled.span<{ priority: 'HIGH' | 'MEDIUM' | 'LOW' }>`
  background-color: ${props => {
    switch (props.priority) {
      case 'HIGH': return Colors.danger;
      case 'MEDIUM': return Colors.warning;
      case 'LOW': return Colors.success;
    }
  }};
  color: ${Colors.textOnPrimary};
  padding: 2px 8px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
`;

const RequisitionMeta = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
  margin-bottom: ${Spacing.sm}px;
`;

const StageIndicators = styled.div`
  display: flex;
  gap: ${Spacing.xs}px;
  align-items: center;
`;

const StageBar = styled.div<{ stage: string; count: number }>`
  flex: 1;
  height: 24px;
  background-color: ${props => {
    switch (props.stage) {
      case 'applied': return Colors.info;
      case 'screening': return Colors.warning;
      case 'interview': return Colors.primary;
      case 'offer': return Colors.success;
      case 'hired': return Colors.accent;
      default: return Colors.borderLight;
    }
  }};
  border-radius: ${BorderRadius.xs}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  opacity: ${props => props.count > 0 ? 1 : 0.3};
`;

const PipelineVisualization = styled.div`
  padding: ${Spacing.lg}px;
`;

const FunnelChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

const FunnelStage = styled.div<{ width: number }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
`;

const FunnelBar = styled.div<{ width: number; color: string }>`
  height: 40px;
  width: ${props => props.width}%;
  background-color: ${props => props.color};
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  padding: 0 ${Spacing.md}px;
  color: ${Colors.textOnPrimary};
  font-weight: ${FontWeights.medium};
  transition: all 150ms ease;
  cursor: pointer;
  
  &:hover {
    transform: translateX(4px);
    box-shadow: ${Shadows.sm};
  }
`;

const FunnelLabel = styled.div`
  min-width: 100px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const AlertsList = styled.div`
  padding: ${Spacing.md}px;
  max-height: 400px;
  overflow-y: auto;
`;

const AlertItem = styled.div<{ priority: 'HIGH' | 'MEDIUM' | 'LOW' }>`
  padding: ${Spacing.md}px;
  border-left: 3px solid ${props => {
    switch (props.priority) {
      case 'HIGH': return Colors.danger;
      case 'MEDIUM': return Colors.warning;
      case 'LOW': return Colors.info;
    }
  }};
  background-color: ${Colors.background};
  border-radius: ${BorderRadius.sm}px;
  margin-bottom: ${Spacing.sm}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AlertTitle = styled.h4`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.xs}px 0;
`;

const AlertDescription = styled.p`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const AlertTime = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const QuickActionsRow = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
`;

const QuickActionButton = styled.button`
  flex: 1;
  padding: ${Spacing.md}px;
  background-color: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: ${Shadows.md};
  }
  
  &:nth-child(2) {
    background-color: ${Colors.secondary};
    
    &:hover {
      opacity: 0.9;
    }
  }
  
  &:nth-child(3) {
    background-color: ${Colors.accent};
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const RecruiterDashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const [metrics] = useState<RecruitmentMetrics>(mockRecruitmentData.metrics);
  const [requisitions] = useState<Requisition[]>(mockRecruitmentData.requisitions);
  const [alerts] = useState<RecruiterAlert[]>(mockRecruitmentData.alerts);

  const handleKPIClick = (kpiType: string) => {
    switch (kpiType) {
      case 'openRequisitions':
        navigate('/requisitions');
        break;
      case 'pipeline':
        navigate('/candidates');
        break;
      default:
        break;
    }
  };

  const handleRequisitionClick = (requisitionId: string) => {
    navigate(`/requisitions/${requisitionId}`);
  };

  const handleStageClick = (stage: string) => {
    navigate(`/candidates?stage=${stage}`);
  };

  const kpiCards = [
    {
      id: 'openReqs',
      title: 'Open Requisitions',
      value: metrics.openRequisitions.toString(),
      change: 3,
      changeType: 'negative' as const,
      icon: '',
      color: Colors.primary,
      description: 'Active job openings',
      onClick: () => handleKPIClick('openRequisitions'),
    },
    {
      id: 'timeToFill',
      title: 'Avg. Time to Fill',
      value: `${metrics.avgTimeToFill} days`,
      change: -5,
      changeType: 'positive' as const,
      icon: '',
      color: Colors.warning,
      description: 'Average hiring timeline',
      onClick: () => handleKPIClick('timeToFill'),
    },
    {
      id: 'pipelineHealth',
      title: 'Pipeline Health Index',
      value: `${metrics.pipelineHealthIndex}%`,
      change: 8,
      changeType: 'positive' as const,
      icon: '',
      color: Colors.success,
      description: 'Overall pipeline quality',
      onClick: () => handleKPIClick('pipeline'),
    },
    {
      id: 'offerAcceptance',
      title: 'Offer Acceptance Rate',
      value: `${metrics.offerAcceptanceRate}%`,
      change: 2,
      changeType: 'positive' as const,
      icon: '',
      color: Colors.accent,
      description: 'Offers accepted vs extended',
      onClick: () => handleKPIClick('offers'),
    },
    {
      id: 'diversity',
      title: 'Diversity Pipeline',
      value: `${metrics.diversityPipelinePercentage}%`,
      change: 5,
      changeType: 'positive' as const,
      icon: '',
      color: Colors.info,
      description: 'Diverse candidate representation',
      onClick: () => handleKPIClick('diversity'),
    },
  ];

  const funnelStages = [
    { 
      stage: 'Applied', 
      count: 245, 
      width: 100, 
      color: Colors.info,
      rate: '100%'
    },
    { 
      stage: 'Screening', 
      count: 180, 
      width: 73, 
      color: Colors.warning,
      rate: `${metrics.conversionRates.appliedToScreen}%`
    },
    { 
      stage: 'Interview', 
      count: 95, 
      width: 39, 
      color: Colors.primary,
      rate: `${metrics.conversionRates.screenToInterview}%`
    },
    { 
      stage: 'Offer', 
      count: 28, 
      width: 11, 
      color: Colors.success,
      rate: `${metrics.conversionRates.interviewToOffer}%`
    },
    { 
      stage: 'Hired', 
      count: 22, 
      width: 9, 
      color: Colors.accent,
      rate: `${metrics.conversionRates.offerToHire}%`
    },
  ];

  return (
    <DashboardContainer>
      <UnifiedPageHeader
        title="Recruiter Dashboard"
        subtitle="Manage requisitions and track your recruitment pipeline"
        metadata={{
          timestamp: new Date(),
          count: requisitions.length,
          status: 'Live'
        }}
        actions={[
          {
            label: 'Time Range',
            onClick: () => console.log('Time range clicked'),
            variant: 'secondary',
            icon: ''
          },
          {
            label: 'Export',
            onClick: () => console.log('Export clicked'),
            variant: 'primary',
            icon: ''
          }
        ]}
      />

      <KPIRow>
        {kpiCards.map((card) => (
          <InteractiveDashboardCard
            key={card.id}
            card={card}
            onPress={card.onClick}
          />
        ))}
      </KPIRow>

      <MainGrid>
        <LeftColumn>
          <SectionCard>
            <SectionHeader>
              <SectionTitle>My Requisitions</SectionTitle>
              <ViewAllButton onClick={() => navigate('/requisitions')}>
                View All →
              </ViewAllButton>
            </SectionHeader>
            <RequisitionsList>
              {requisitions.slice(0, 4).map((req) => (
                <RequisitionItem 
                  key={req.id}
                  onClick={() => handleRequisitionClick(req.id)}
                >
                  <RequisitionHeader>
                    <RequisitionTitle>{req.title}</RequisitionTitle>
                    <PriorityBadge priority={req.priority}>
                      {req.priority}
                    </PriorityBadge>
                  </RequisitionHeader>
                  <RequisitionMeta>
                    <span>{req.location}</span>
                    <span>{req.daysOpen} days open</span>
                    <span>{req.candidateCount} candidates</span>
                  </RequisitionMeta>
                  <StageIndicators>
                    <StageBar stage="applied" count={req.stageCount.applied}>
                      {req.stageCount.applied}
                    </StageBar>
                    <StageBar stage="screening" count={req.stageCount.screening}>
                      {req.stageCount.screening}
                    </StageBar>
                    <StageBar stage="interview" count={req.stageCount.interview}>
                      {req.stageCount.interview}
                    </StageBar>
                    <StageBar stage="offer" count={req.stageCount.offer}>
                      {req.stageCount.offer}
                    </StageBar>
                    <StageBar stage="hired" count={req.stageCount.hired}>
                      {req.stageCount.hired}
                    </StageBar>
                  </StageIndicators>
                </RequisitionItem>
              ))}
            </RequisitionsList>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionTitle>Pipeline Visualization</SectionTitle>
              <ViewAllButton onClick={() => navigate('/candidates')}>
                View Details →
              </ViewAllButton>
            </SectionHeader>
            <PipelineVisualization>
              <FunnelChart>
                {funnelStages.map((stage) => (
                  <FunnelStage key={stage.stage} width={stage.width}>
                    <FunnelLabel>{stage.stage}</FunnelLabel>
                    <FunnelBar 
                      width={stage.width} 
                      color={stage.color}
                      onClick={() => handleStageClick(stage.stage.toLowerCase())}
                    >
                      {stage.count} ({stage.rate})
                    </FunnelBar>
                  </FunnelStage>
                ))}
              </FunnelChart>
            </PipelineVisualization>
          </SectionCard>
        </LeftColumn>

        <RightColumn>
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Alerts & To-Dos</SectionTitle>
              <ViewAllButton>Mark All Read</ViewAllButton>
            </SectionHeader>
            <AlertsList>
              {alerts.map((alert) => (
                <AlertItem key={alert.id} priority={alert.priority}>
                  <AlertTitle>{alert.title}</AlertTitle>
                  <AlertDescription>{alert.description}</AlertDescription>
                  <AlertTime>{alert.timestamp}</AlertTime>
                </AlertItem>
              ))}
            </AlertsList>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionTitle>Quick Actions</SectionTitle>
            </SectionHeader>
            <QuickActionsRow>
              <QuickActionButton onClick={() => navigate('/candidates/add')}>
                + Add Candidate
              </QuickActionButton>
              <QuickActionButton onClick={() => navigate('/requisitions/create')}>
                + Create Requisition
              </QuickActionButton>
              <QuickActionButton onClick={() => navigate('/jobs/post')}>
                + Post Job
              </QuickActionButton>
            </QuickActionsRow>
          </SectionCard>

          <SectionCard>
            <SectionHeader>
              <SectionTitle>Market Intelligence</SectionTitle>
              <ViewAllButton onClick={() => navigate('/market-intelligence')}>
                View Details →
              </ViewAllButton>
            </SectionHeader>
            <div style={{ padding: `${Spacing.lg}px` }}>
              <div style={{ marginBottom: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary, marginBottom: `${Spacing.xs}px` }}>
                  Company Sentiment
                </div>
                <div style={{ 
                  fontSize: `${FontSizes.xxl}px`, 
                  fontWeight: FontWeights.bold,
                  color: getSentimentColor(mockMarketIntelligence.sentimentAnalysis.current.overallSentiment)
                }}>
                  {mockMarketIntelligence.sentimentAnalysis.current.overallSentiment}%
                </div>
                <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.success }}>
                  ↑ {mockMarketIntelligence.sentimentAnalysis.current.change}% vs last week
                </div>
              </div>
              
              <div style={{ borderTop: `1px solid ${Colors.borderLight}`, paddingTop: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary, marginBottom: `${Spacing.sm}px` }}>
                  Critical Roles to Fill
                </div>
                {mockMarketIntelligence.replacementDifficulty
                  .filter(r => r.difficultyLevel === 'CRITICAL')
                  .slice(0, 2)
                  .map(role => (
                    <div key={role.role} style={{ 
                      padding: `${Spacing.xs}px`,
                      marginBottom: `${Spacing.xs}px`,
                      backgroundColor: Colors.danger + '10',
                      borderRadius: `${BorderRadius.sm}px`,
                      fontSize: `${FontSizes.xs}px`
                    }}>
                      <strong style={{ color: Colors.danger }}>{role.role}</strong>
                      <div style={{ color: Colors.textSecondary }}>
                        Only {role.marketAvailability} candidates available
                      </div>
                    </div>
                  ))
                }
              </div>
              
              <div style={{ borderTop: `1px solid ${Colors.borderLight}`, paddingTop: `${Spacing.md}px`, marginTop: `${Spacing.md}px` }}>
                <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.textSecondary, marginBottom: `${Spacing.sm}px` }}>
                  Top Competitor Activity
                </div>
                <div style={{ fontSize: `${FontSizes.sm}px`, color: Colors.textPrimary }}>
                  <strong>TechCorp</strong> - 145 hires this quarter
                </div>
                <div style={{ fontSize: `${FontSizes.xs}px`, color: Colors.warning }}>
                  ↑ Increasing hiring activity
                </div>
              </div>
            </div>
          </SectionCard>
        </RightColumn>
      </MainGrid>
    </DashboardContainer>
  );
};

export default RecruiterDashboardScreen;